import TextOptions from "%/dynamic-components/render-engine/bundles/text-options";
import { hasUnfilledPlaceholder } from "%/dynamic-component-editor/standard-elements/click-actions/resolve-context-href";
import { ContextHrefMixin } from "%/dynamic-component-editor/standard-elements/click-actions/context-href-mixin";
import { mapActions, mapState, mapWritableState } from "pinia";
import useDynamicComponentBuilderStore from "%/stores/dynamic-component-builder-store";
import { useGeneralStore } from "%/stores/general-store";
import { navigateBack } from "%/dynamic-components/render-engine/helpers/navigate-back";
import TextOption from "%/dynamic-components/render-engine/options/text-options/text-option";
import TextWrap from "%/dynamic-components/render-engine/options/text-options/text-wrap";
import HrefOption from "%/dynamic-components/render-engine/options/url-options/href-option";
import TextAlign from "%/dynamic-components/render-engine/options/text-options/text-align";
import Ellipsis from "%/dynamic-components/render-engine/options/text-options/ellipsis";
import TextDecoration from "%/dynamic-components/render-engine/options/text-options/text-decoration";
import Padding from "%/dynamic-components/render-engine/options/spacing-options/padding";
import Margins from "%/dynamic-components/render-engine/options/spacing-options/margins";
import BaseOption from "%/dynamic-components/render-engine/classes/base-option";
import FontFamily from "%/dynamic-components/render-engine/options/text-options/font-family";
import FontSize from "%/dynamic-components/render-engine/options/text-options/font-size";
import FontColor from "%/dynamic-components/render-engine/options/text-options/font-color";
import normalizeFontSizeStyleValue from "%/dynamic-component-editor/helpers/font-size-style-value";
import FontWeight from "%/dynamic-components/render-engine/options/text-options/font-weight";
import FontCase from "%/dynamic-components/render-engine/options/text-options/font-case";
import SpacingOptions from "%/dynamic-components/render-engine/bundles/spacing-options";
import FontStyle from "%/dynamic-components/render-engine/options/text-options/font-style";
import LineHeightMultiplier from "%/dynamic-components/render-engine/options/text-options/line-height-multiplier";
import AppearanceOptions from "%/dynamic-components/render-engine/bundles/appearance-options";
import BasicTextOptions from "%/dynamic-components/render-engine/bundles/basic-text-options";
import BrandedTextStyleOptions from "%/dynamic-components/render-engine/bundles/branded-text-style-options";
import LayoutWidth from "%/dynamic-components/render-engine/options/layout-options/layout-width";
import BackgroundColor from "%/dynamic-components/render-engine/options/background-options/background-color";
import BackgroundOptions from "%/dynamic-components/render-engine/bundles/background-options";
import Label from "%/dynamic-components/render-engine/options/url-options/label";
import EffectsOptions from "%/dynamic-components/render-engine/bundles/effects-options";
import BoxShadow from "%/dynamic-components/render-engine/options/box-shadow-options/box-shadow";
import BorderSides from "%/dynamic-components/render-engine/options/border-options/border-sides";
import MaxWidth from "%/dynamic-components/render-engine/options/spacing-options/max-width";
import MinWidth from "%/dynamic-components/render-engine/options/spacing-options/min-width";
import ClickAction from "%/dynamic-components/render-engine/options/click-actions/click-action";
import LinkType from "%/dynamic-components/render-engine/options/link-type";
import { resolveContextualPageValue } from "%/dynamic-components/render-engine/options/click-actions/contextual-page-value";
import DynamicComponentError from "%/errors/dynamic-component-error";
import TextShadow from "%/dynamic-components/render-engine/options/text-shadow-options/text-shadow";
import TextOverflow from "%/dynamic-components/render-engine/options/text-options/text-overflow";
import MaxVerticalLines from "%/dynamic-components/render-engine/options/text-options/max-vertical-lines";
import { useCollaboratorStore } from "~/stores/collaborator-store";
import LayoutHeight from "%/dynamic-components/render-engine/options/layout-options/layout-height";
import { GestureActionsMixin } from "%/dynamic-component-editor/standard-elements/click-actions/gesture-actions-mixin";

const CLICK_BUFFER = 1000;

export const TextElementMixin = {
  mixins: [ContextHrefMixin, GestureActionsMixin],
  elementPanelSettings: {
    displayIcon: "text",
    elementType: "element",
    elementGroup: "general",
  },
  optionBundles: [
    new AppearanceOptions({}, {
      "border-radius": false,
    }),
    BrandedTextStyleOptions,
    BasicTextOptions,
    TextOptions,
    EffectsOptions,
    new SpacingOptions({
      "default.padding": {
        "top": null,
        "left": null,
        "bottom": null,
        "right": null,
      },
    }),
    new BackgroundOptions({}, {
      "background-image": true,
      "background-gradient": true,
    }),
  ],
  options: [
    ClickAction,
  ],
  data() {
    return {
      initialValue: "",
      TextOption,
      TextWrap,
      TextAlign,
      Ellipsis,
      FontCase,
      HrefOption,
      lastClicked: 0,
      editable: false,
      relatedElement: null,
      debounce: null,
      showLinkModal: false,
      linkData: {},
      originalLinkData: {},
    };
  },
  computed: {
    ...mapWritableState(useDynamicComponentBuilderStore, [
      "currentlyInspectedElementIdentifiers",
      "dynamicComponentName",
      "isPage",
    ]),
    ...mapState(useCollaboratorStore, [
      "canEditPublication",
    ]),
    fontColor() {
      return this.getOptionValue(FontColor);
    },
    fontSizeNumberValue() {
      return parseInt(this.getOptionValue(FontSize).replace("px", ""));
    },
    myWidth() {
      if (!this.getOptionValue(TextOverflow) && !this.getOptionValue(TextWrap)) {
        if (typeof this.getOptionValue(LayoutWidth) !== "number") {
          return "fill";
        }
      }
      return this.getOptionValue(LayoutWidth);
    },
    myHeight() {
      return this.getOptionValue(LayoutHeight);
    },
    fullTextOptionsAttributes() {
      return {
        width: this.myWidth,
        height: this.myHeight,
        maxWith: this.getOptionValue(MaxWidth),
        minWith: this.getOptionValue(MinWidth),
        ...this.textOnlyAttributes,
        textStyleOverrides: this.textStyleOverrides,
      };
    },
    textOnlyAttributes() {
      return {
        overflow: this.getOptionValue(TextOverflow),
        maxVerticalLines: this.getOptionValue(MaxVerticalLines),
        wrap: this.getOptionValue(TextWrap),
        textAlign: this.getOptionValue(TextAlign),
        textCase: this.getOptionValue(FontCase),
        lineHeightMultiplier: this.getOptionValue(LineHeightMultiplier),
        textStyleOverrides: this.textOnlyStyleOverrides,
      };
    },
    textOnlyStyleOverrides() {
      return {
        standardFontFamily: this.getOptionValue(FontFamily),
        standardFontSize: normalizeFontSizeStyleValue(this.getOptionValue(FontSize), { element: "TextElement", option: "font-size" }),
        standardFontColor: this.getOptionValue(FontColor),
        standardFontWeight: this.getOptionValue(FontWeight),
        standardHoverFontFamily: this.getOptionValue(FontFamily, BaseOption.$states.HOVER),
        standardHoverFontSize: normalizeFontSizeStyleValue(this.getOptionValue(FontSize, BaseOption.$states.HOVER), { element: "TextElement", option: "hover.font-size" }),
        standardHoverFontColor: this.getOptionValue(FontColor, BaseOption.$states.HOVER),
        standardHoverFontWeight: this.getOptionValue(FontWeight, BaseOption.$states.HOVER),
        textDecoration: this.getOptionValue(TextDecoration),
        textDecorationHover: this.getOptionValue(TextDecoration, BaseOption.$states.HOVER),
        textStyle: this.getOptionValue(FontStyle),
        textStyleHover: this.getOptionValue(FontStyle, BaseOption.$states.HOVER),
      };
    },
    spacingStyleOverrides() {
      return {
        paddingTop: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "top") || "none",
        paddingBottom: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "bottom") || "none",
        paddingLeft: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "left") || "none",
        paddingRight: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "right") || "none",
        marginTop: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "top") || "none",
        marginBottom: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "bottom") || "none",
        marginLeft: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "left") || "none",
        marginRight: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "right") || "none",
        paddingHoverTop: this.getOptionValue(Padding, BaseOption.$states.HOVER, "top") || "none",
        paddingHoverBottom: this.getOptionValue(Padding, BaseOption.$states.HOVER, "bottom") || "none",
        paddingHoverLeft: this.getOptionValue(Padding, BaseOption.$states.HOVER, "left") || "none",
        paddingHoverRight: this.getOptionValue(Padding, BaseOption.$states.HOVER, "right") || "none",
        marginHoverTop: this.getOptionValue(Margins, BaseOption.$states.HOVER, "top") || "none",
        marginHoverBottom: this.getOptionValue(Margins, BaseOption.$states.HOVER, "bottom") || "none",
        marginHoverLeft: this.getOptionValue(Margins, BaseOption.$states.HOVER, "left") || "none",
        marginHoverRight: this.getOptionValue(Margins, BaseOption.$states.HOVER, "right") || "none",
      };
    },
    textStyleOverrides() {
      return {
        standardFontFamily: this.getOptionValue(FontFamily),
        standardFontSize: normalizeFontSizeStyleValue(this.getOptionValue(FontSize), { element: "TextElement", option: "font-size" }),
        standardFontColor: this.getOptionValue(FontColor),
        standardFontWeight: this.getOptionValue(FontWeight),
        standardHoverFontFamily: this.getOptionValue(FontFamily, BaseOption.$states.HOVER),
        standardHoverFontSize: normalizeFontSizeStyleValue(this.getOptionValue(FontSize, BaseOption.$states.HOVER), { element: "TextElement", option: "hover.font-size" }),
        standardHoverFontColor: this.getOptionValue(FontColor, BaseOption.$states.HOVER),
        standardHoverFontWeight: this.getOptionValue(FontWeight, BaseOption.$states.HOVER),
        ...this.spacingStyleOverrides,
        backgroundColor: this.getOptionValue(BackgroundColor),
        textDecoration: this.getOptionValue(TextDecoration),
        textDecorationHover: this.getOptionValue(TextDecoration, BaseOption.$states.HOVER),
        textStyle: this.getOptionValue(FontStyle),
        textStyleHover: this.getOptionValue(FontStyle, BaseOption.$states.HOVER),
        hoverBackgroundColor: this.getOptionValue(BackgroundColor, BaseOption.$states.HOVER),
        boxShadow: this.getOptionValue(BoxShadow),
        boxShadowHover: this.getOptionValue(BoxShadow, BaseOption.$states.HOVER),
        textShadow: this.getOptionValue(TextShadow),
        textShadowHover: this.getOptionValue(TextShadow, BaseOption.$states.HOVER),
      };
    },
    textIsEmpty() {
      return this.initialValue.body_text === "\n" || !this.initialValue.body_text;
    },
    textOptionValue() {
      let textValue = this.getOptionValue(this.textValueOptionClass) || "";
      if (this.textValueKey && textValue) {
        // handle backwards compat
        if (this.textValueKey === "label") {
          // A present label wins even if hasUpdated was lost; fall back to the
          // legacy Label option only when the label is genuinely empty.
          const label = textValue[this.textValueKey];
          const hasLabel = typeof label === "string" && label.length > 0;
          textValue = textValue.hasUpdated || hasLabel
            ? label
            : this.getOptionValue(Label);
        } else {
          textValue = textValue[this.textValueKey];
        }
      }

      return textValue;
    },
    previewMode() {
      return !this.editable || !this.currentlyInspectedElementIdentifiers.includes(this.identifier);
    },
    hasTextContent() {
      if (typeof this.initialValue === "string") {
        return this.initialValue.length > 0;
      }
      return this.initialValue.body_text.length > 0 && this.initialValue.body_text !== "\n";
    },
    textValueOptionClass() {
      return TextOption;
    },
    textValueKey() {
      return undefined;
    },
    myBorderSides() {
      return this.getOptionValue(BorderSides);
    },
    // Entity-page link seam: entity-bound subclasses (the DynamicApp*
    // elements) override this to the resolved display-page URL of their
    // bound entity (the `link-to-entity-page` option). When set it takes
    // precedence over an authored ClickAction in actionType / clickValue
    // below. Base text elements have no entity context — null.
    entityPageHref() {
      return null;
    },
    actionType() {
      if (this.entityPageHref) {
        return "goToUrl";
      }
      return this.getOptionValue(ClickAction)?.action;
    },
    // Inline rather than a scoped class: subclasses (Label, Heading, ...) inherit
    // this template through `extends` but render under their own scope id.
    clickableStyle() {
      return this.hasClickValue ? { cursor: "pointer" } : null;
    },
    hasClickValue() {
      if (this.editMode) {
        return false;
      }

      return this.clickValue !== null && this.clickValue?.length > 0;
    },
    clickValue() {
      if (this.entityPageHref) {
        return this.entityPageHref;
      }
      let urlValue = this.getOptionValue(ClickAction)?.value;

      if (!this.linkType) {
        return;
      }

      switch (this.linkType) {
        case LinkType.LINK_TYPE_HYPERLINK:
          return urlValue;
        case LinkType.LINK_TYPE_PAGE: {
          const contextHref = this.contextResolvedHref(
            this.getOptionValue(ClickAction), urlValue, this.links.web,
          );
          if (contextHref) {
            return contextHref;
          }
          urlValue = resolveContextualPageValue(urlValue);
          if (urlValue === null || urlValue === undefined) {
            return;
          }
          if (urlValue.startsWith("/")) {
            urlValue = urlValue.substring(1);
          }
          const pageHref = `${this.links.web}/${urlValue}`;
          return hasUnfilledPlaceholder(pageHref) ? undefined : pageHref;
        }
        case LinkType.LINK_TYPE_STORY:
          return `${this.links.web}/story/${urlValue}`;
        case LinkType.LINK_TYPE_HASHTAG:
          return `${this.links.web}/stories/${urlValue}`;
        default:
          throw new DynamicComponentError("Url type not found", {
            linkType: this.linkType,
            urlValue,
          });
      }
    },
    linkType() {
      return this.getOptionValue(ClickAction)?.linkType;
    },
    openInNewTab() {
      return this.getOptionValue(ClickAction)?.openInNewTab;
    },
  },
  watch: {
    textOptionValue: {
      immediate: true,
      handler(newValue) {
        this.initialValue = newValue;
      },
    },
    previewMode(newValue) {
      if (newValue) {
        this.$refs.richTextElement.commands.closeBubbleMenu();
        useDynamicComponentBuilderStore().removeElementFromEditingInline(this.identifier);
      }
    },
  },
  created() {
    // this.setDefaultBrandingValue();
  },
  methods: {
    ...mapActions(useDynamicComponentBuilderStore, [
      "findElementInLayout",
      "handleOnEdit",
    ]),
    handleLinkModalOpen(event) {
      this.linkData = event;
      this.originalLinkData = event;
      this.showLinkModal = true;
    },
    handleLinkModalClose() {
      this.linkData = {};
      this.originalLinkData = {};
      this.showLinkModal = false;
    },
    setDefaultBrandingValue() {
      if (!this.configuration.options["default.text-branding-style-option"]) {
        this.configuration.options["default.text-branding-style-option"] = "paragraph";
      }
    },
    handleBlur() {
      this.editable = false;
    },
    handleEnter(event) {
      if (event.shiftKey) {
        this.handleBlur();
      }
    },
    handleClick(event) {
      if (this.editMode && this.canEditPublication) {
        this.setElementToInspect(event);
        if (Date.now() - this.lastClicked < CLICK_BUFFER && !this.editable) {
          this.editable = true;
          this.relatedElement = { ...this.dynamicComponentInstance.getElementById(this.identifier) };
          useDynamicComponentBuilderStore().setElementAsEditingInline(this.identifier);
          requestAnimationFrame(() => {
            const editableContent = this.$el.querySelector(".i-text.editor-content");
            if (editableContent) {
              editableContent.focus();

              const range = document.createRange();
              range.selectNodeContents(editableContent);

              const selection = window.getSelection();
              selection.removeAllRanges();
              selection.addRange(range);
            }
          });
        }
        this.lastClicked = Date.now();
      } else {
        this.handleClickAction();
      }
    },
    resolveClickActionHref(value) {
      if (!value || /^[a-z][a-z0-9+.-]*:/i.test(value) || value.startsWith("//")) {
        return value;
      }
      if (!value.startsWith("/")) {
        return value;
      }
      const webBase = useGeneralStore().links.web || "";
      return `${webBase}${value}`;
    },
    handleClickAction() {
      // A click that is authored as a gesture belongs to the gesture engine,
      // which runs the same action: doing it here as well navigates twice, or
      // opens two tabs.
      if (this.gestureOwnsTheClick) {
        return;
      }
      if (this.actionType === "goBack") {
        navigateBack(useGeneralStore().links.web);
        return;
      }
      if (this.actionType === "goToUrl") {
        const a = document.createElement("a");
        if (this.openInNewTab) {
          a.target = "_blank";
        }

        if (!this.clickValue) {
          return;
        }

        a.href = this.resolveClickActionHref(this.clickValue);
        // A detached anchor ignores target="_blank" — it has to be in the
        // document for the browser to honour it.
        if (this.openInNewTab) {
          a.rel = "noopener";
          document.body.appendChild(a);
          a.click();
          a.remove();
          return;
        }
        a.click();
      } else {
        // ignore for now
      }
    },
    setElementToInspect(event) {
      event.stopPropagation();
      this.handleOnEdit(this.identifier, event);
      useDynamicComponentBuilderStore().setActiveBreakpointByAlias(this.breakpoint);
    },
    handleTextOptionUpdate(value) {
      if (this.editMode) {
        this.initialValue = value;
        let valueToUpdate = value;

        if (this.textValueKey) {
          valueToUpdate = JSON.parse(JSON.stringify(this.getOptionValue(this.textValueOptionClass)));
          valueToUpdate[this.textValueKey] = value;
          valueToUpdate["hasUpdated"] = true;
        }

        const [state, breakpoint] = useDynamicComponentBuilderStore().activeState.split(":");
        this.dynamicComponentInstance.modifyElementOption(
          this.identifier,
          "text-option",
          valueToUpdate,
          breakpoint,
          state,
        );
      }
    },
  },
};
